import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../App';

const benefits = [
  'View complete deal details & financials',
  'Access the service provider directory',
  'Save your favorite properties',
  'Contact deal posters directly',
  'Get personalized deal recommendations',
  'Post your own deals'
];

export default function AuthGate({ title = "Sign Up Free to Continue", children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5DADE2]"></div>
      </div>
    );
  }

  // If user is authenticated, show the content
  if (user) {
    return children;
  }

  // Show the auth gate
  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-[#5DADE2]" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="auth-gate-title">
            {title}
          </h2>
          <p className="text-slate-600">
            Create a free account to unlock full access to NextFlip
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 text-left">
          <h3 className="font-semibold text-slate-900 mb-4">What you'll get:</h3>
          <ul className="space-y-3">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-3 text-slate-600">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            asChild 
            size="lg" 
            className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full px-8"
            data-testid="auth-gate-signup-btn"
          >
            <Link to="/signup" state={{ from: location }}>
              Sign Up Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button 
            asChild 
            size="lg" 
            variant="outline" 
            className="rounded-full px-8"
            data-testid="auth-gate-login-btn"
          >
            <Link to="/login" state={{ from: location }}>
              Log In
            </Link>
          </Button>
        </div>

        <p className="text-sm text-slate-500 mt-4">
          Already have an account? <Link to="/login" state={{ from: location }} className="text-[#5DADE2] hover:underline">Log in here</Link>
        </p>
      </div>
    </div>
  );
}
